import '../../styles/auth.css';
import {
  listTiers,
  checkout,
  loadUser,
  type TierInfo,
  type User,
} from '../../auth';
import { bootShell, showError, clearMessages, describeError, toast } from './_shell';
import { attachCheckoutReturnHandler } from '../../modules/checkout-return';

bootShell();
// Surfaces ?checkout=cancel (and ?checkout=success in the unlikely case
// Stripe routes back here instead of the home page).
attachCheckoutReturnHandler();

type Cycle = 'monthly' | 'yearly';
let cycle: Cycle = 'monthly';
let tiers: TierInfo[] = [];
let currentUser: User | null = null;

const grid = document.getElementById('pricing-grid')!;
const cycleMonthlyBtn = document.getElementById('cycle-monthly')!;
const cycleYearlyBtn = document.getElementById('cycle-yearly')!;

cycleMonthlyBtn.addEventListener('click', () => { cycle = 'monthly'; updateCycleUI(); renderTiers(); });
cycleYearlyBtn.addEventListener('click', () => { cycle = 'yearly'; updateCycleUI(); renderTiers(); });

function updateCycleUI() {
  cycleMonthlyBtn.classList.toggle('active', cycle === 'monthly');
  cycleYearlyBtn.classList.toggle('active', cycle === 'yearly');
}

function formatPrice(usd: number): string {
  if (usd === 0) return 'Free';
  // Show $X for whole, $X.YY for fractional.
  if (Number.isInteger(usd)) return `$${usd}`;
  return `$${usd.toFixed(2)}`;
}

/** Per-month rate, even when displaying the yearly column. */
function monthlyEquivalent(tier: TierInfo, c: Cycle): number {
  if (c === 'monthly') return tier.price_monthly;
  // Yearly is total cost / 12 to show the "effective per-month" rate.
  return tier.price_yearly / 12;
}

function priceMeta(tier: TierInfo, c: Cycle): string {
  if (tier.price_monthly === 0 && tier.price_yearly === 0) return 'Forever';
  if (c === 'yearly') return `$${tier.price_yearly}/yr billed annually`;
  return 'Billed monthly';
}

function ctaLabel(tier: TierInfo): string {
  // Already on this tier (active subscription)
  if (currentUser
      && currentUser.subscription_tier === tier.key
      && (currentUser.subscription_status === 'active' || currentUser.subscription_status === 'trial')) {
    return currentUser.subscription_status === 'trial' ? 'Current plan (trial)' : 'Current plan';
  }
  if (tier.key === 'free') {
    return currentUser ? 'Included with account' : 'Get started free';
  }
  if (tier.trial_days > 0 && (!currentUser || currentUser.subscription_status === 'none')) {
    return `Start ${tier.trial_days}-day trial`;
  }
  return 'Upgrade';
}

function ctaDisabled(tier: TierInfo): boolean {
  if (!currentUser) return false;
  if (currentUser.subscription_tier === tier.key
      && (currentUser.subscription_status === 'active' || currentUser.subscription_status === 'trial')) {
    return true;
  }
  return false;
}

/** Featured = pro tier (matrix-green emphasis per spec). */
function isFeatured(tier: TierInfo): boolean {
  return tier.key === 'pro';
}

function featureLines(tier: TierInfo): string[] {
  // Pull features off the products map, falling back to a short stock list.
  const products = tier.products?.aiboard || [];
  if (products.length) {
    return products.map(slugToLabel);
  }
  if (tier.key === 'free') {
    return [
      'BYOK across every supported model',
      'Single-run comparison',
      'Save & export responses',
    ];
  }
  if (tier.key === 'pro') {
    return [
      'Unlimited parallel runs',
      'Synthesis + optimal-prompt distillation',
      'Priority routing & exports',
    ];
  }
  return [
    'Everything in Pro',
    'Team workspace & shared library',
    'SSO and admin controls',
  ];
}

function slugToLabel(slug: string): string {
  return slug
    .replace(/^aiboard\./, '')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderTiers() {
  if (!tiers.length) return;
  grid.innerHTML = '';

  // Order: free → pro → team (or whatever the API returns, but free first).
  const sorted = [...tiers].sort((a, b) => {
    const order = (k: string) => (k === 'free' ? 0 : k === 'pro' ? 1 : k === 'team' ? 2 : 3);
    return order(a.key) - order(b.key);
  });

  for (const tier of sorted) {
    const card = document.createElement('div');
    card.className = 'pricing-card' + (isFeatured(tier) ? ' featured' : '');

    const isFree = tier.price_monthly === 0 && tier.price_yearly === 0;
    const monthly = monthlyEquivalent(tier, cycle);

    card.innerHTML = `
      <div class="tier-name">${escapeHtml(tier.label)}</div>
      <div class="tier-tagline">${escapeHtml(tier.tagline || '')}</div>
      <div class="price-line">
        <span class="price-amount${isFree ? ' free' : ''}">${escapeHtml(formatPrice(monthly))}</span>
        ${!isFree ? `<span class="price-unit">/ mo</span>` : ''}
      </div>
      ${!isFree ? `<div class="price-meta">${escapeHtml(priceMeta(tier, cycle))}</div>` : `<div class="price-meta">No card required</div>`}
      <ul class="feature-list">
        ${featureLines(tier).map((f) => `<li>${escapeHtml(f)}</li>`).join('')}
      </ul>
      <div class="cta">
        ${renderCtaButton(tier)}
      </div>
    `;
    grid.appendChild(card);
  }

  // Wire CTAs
  grid.querySelectorAll<HTMLButtonElement>('[data-cta-tier]').forEach((btn) => {
    btn.addEventListener('click', () => onCta(btn.dataset.ctaTier as string));
  });
}

function renderCtaButton(tier: TierInfo): string {
  const label = ctaLabel(tier);
  const disabled = ctaDisabled(tier);
  if (tier.key === 'free') {
    if (currentUser) {
      return `<a class="auth-btn-secondary" href="/">${escapeHtml(label)}</a>`;
    }
    return `<a class="auth-btn-secondary" href="/register.html">${escapeHtml(label)}</a>`;
  }
  return `<button class="auth-btn-primary" data-cta-tier="${escapeAttr(tier.key)}" ${disabled ? 'disabled' : ''}>${escapeHtml(label)}</button>`;
}

async function onCta(tierKey: string) {
  clearMessages();

  // Free tier handled via the anchor element — only paid tiers reach here.
  if (tierKey === 'free') return;

  if (!currentUser) {
    // Bounce through register so the trial fires for them on signup.
    const next = `/pricing.html`;
    location.href = `/register.html?next=${encodeURIComponent(next)}`;
    return;
  }

  if (!currentUser.email_verified) {
    showError('Verify your email before starting a trial. Check your inbox or resend from your account page.');
    return;
  }

  const btn = grid.querySelector<HTMLButtonElement>(`[data-cta-tier="${tierKey}"]`);
  if (btn) btn.disabled = true;

  try {
    const res = await checkout(tierKey as 'pro' | 'team', cycle);
    if (res.url) {
      location.href = res.url;
      return;
    }
    toast('Could not open Stripe Checkout.');
    if (btn) btn.disabled = false;
  } catch (err: any) {
    if (err?.status === 503) {
      toast('Stripe Checkout is coming soon.');
    } else if (err?.status === 409) {
      toast('You already have an active subscription. Use Manage Subscription instead.');
    } else if (err?.status === 401) {
      location.href = `/login.html?next=${encodeURIComponent('/pricing.html')}`;
      return;
    } else {
      showError(describeError(err, 'Could not start checkout.'));
    }
    if (btn) btn.disabled = false;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as Record<string, string>)[c]);
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/'/g, '&#39;');
}

async function init() {
  // Both calls fire in parallel — user load is best-effort, tier list is required.
  const [userResult, tiersResult] = await Promise.allSettled([
    loadUser(),
    listTiers(),
  ]);

  if (userResult.status === 'fulfilled') currentUser = userResult.value;

  if (tiersResult.status === 'fulfilled') {
    tiers = tiersResult.value.tiers || [];
    if (tiers.length === 0) {
      grid.innerHTML = '<div class="pricing-card"><div class="tier-name">No plans available right now.</div></div>';
    } else {
      renderTiers();
    }
  } else {
    grid.innerHTML = '<div class="pricing-card"><div class="tier-name">Could not load pricing.</div><div class="tier-tagline">Please refresh in a moment.</div></div>';
    showError(describeError(tiersResult.reason, 'Could not load pricing.'));
  }
}

init();
